import { NgModule, Inject } from '@angular/core';
import { StoreModule, ActionReducer, Store, ActionsSubject } from '@ngrx/store';
import { ROUTER_NAVIGATION, ROUTER_ERROR, ROUTER_NAVIGATION_CANCEL, ROUTER_CANCEL } from '../action-types';
import { reducer } from './router-reducer';
import { initialRouterState, IAppRouterState } from './router-state';
import { EffectsModule } from '@ngrx/effects';
import { RouterEffects } from './router-effects';

import { filter, withLatestFrom } from 'rxjs/operators';
import { getRouterState } from './router-selectors';
import { RouterService } from './router.service';
import { StateDeclaration, StateObject, Transition, UIRouter } from '@uirouter/core';
import {
    RouterNavigationCancelAction,
    RouterNavigationSuccessAction,
    RouterToChildNavigation,
    SetRouterStateAction
} from './router-actions';
import { StateSlice } from '../state-slice';
import { combineReducers } from '@core/utils/state';

@NgModule({
  imports: [
    StoreModule.forFeature(StateSlice.ROUTER, (reducer as ActionReducer<any, any>), {
      initialState: initialRouterState,
      reducerFactory: combineReducers
    }),
    EffectsModule.forFeature([RouterEffects])
  ],
  declarations: [],
  providers: [
    RouterService
  ]
})
export class RouterStateModule {

  private routerState: any;
  private dispatchTriggeredByRouter: boolean;
  private navigationTriggeredByDispatch: boolean;
  private lastRoutesRecognized: StateDeclaration;
  private lastParams: any;
  private listenTo: string[] = [ROUTER_NAVIGATION_CANCEL];
  private cancelNavigation: string;
  private cancelNavigationOptions: any;
  private fromUrl: string;
  private fromQueryParams: any;
  private fromParams: any;
  private params: any;

  constructor(@Inject(UIRouter) private router: UIRouter,
              @Inject(Store) private store$: Store<IAppRouterState>,
              private actionsSubject$: ActionsSubject) {

    // this.router.events
    //   .pipe(withLatestFrom(this.store$.select(getRouterState)))
    //   .subscribe(([event, routerState]: [any, IAppRouterState]) => {

    //     if (event instanceof NavigationStart) {
    //       const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;
    //       this.fromUrl = snapshot.url;
    //       this.fromParams = routerState.params;
    //       this.fromQueryParams = routerState.queryParams;
    //     }

    //     if (event instanceof ActivationEnd && (Object.keys(event.snapshot.params).length)) {

    //       // Patch parameters in router state
    //       this.params = event.snapshot?.params || {};
    //     }

    //     if (event instanceof NavigationEnd) {
    //       const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;

    //       try {
    //         this.store$.dispatch({ type: ROUTER_NAVIGATION, payload: {
    //           routerState: {
    //             url: snapshot.url,
    //             params: this.params,
    //             queryParams: snapshot.root.queryParams,
    //             fromState: {
    //               url: this.fromUrl,
    //               params: this.fromParams
    //             }
    //           }
    //         }});

    //       } finally {
    //         this.dispatchTriggeredByRouter = false;
    //         this.navigationTriggeredByDispatch = false;
    //       }
    //     }

    //     if (event instanceof NavigationError) {
    //       // Present error to user
    //       console.error(event.error);

    //       try {

    //         this.store$.dispatch({ type: ROUTER_ERROR, payload: { error: event.error }});

    //       } catch (error) {
    //         console.error(error);
    //       }
    //     }

        
    //   });
  
        this.dispatchTriggeredByRouter = false;
        this.navigationTriggeredByDispatch = false;
        this.initRouterHooks();
        this.initData();
    }

    /**
     * Listen for store state change.
     */
    initData() {
        this.store$.select(getRouterState).subscribe((s) => {
            this.routerState = s;
            this.navigateIfNeeded();
        });
    }

    /**
     * @return {?}
     */
    shouldDispatchRouterNavigation() {
        if (!this.routerState) {
            return true;
        }

        return !this.navigationTriggeredByDispatch;
    }

    /**
     * @return {?}
     */
    navigateIfNeeded() {
        if (!this.routerState || !this.routerState.state) {
            return;
        }

        if (!this.dispatchTriggeredByRouter) {
            if (this.router.urlService.url() !== this.routerState.state.url) {
                this.navigationTriggeredByDispatch = true;
                this.router.stateService.go(this.routerState.state.name);
            }
        }
    }

    /**
    * Hook into the router change events.
    */
    private initRouterHooks() {
        this.actionsSubject$
            .pipe(filter((action) => this.listenTo.indexOf(action.type) >= 0))
            .subscribe((action: RouterNavigationCancelAction) =>
                this.shouldCancelNavigation(
                    action.payload.state,
                    action.payload.options
                )
            );

        this.router.transitionService.onEnter({}, (trans: Transition): any => {

            // TODO: Rename action to ROUTER_ENTER
            // TODO: Cleanup this hack to stop route events per route level, extend Actions to support filter at action level
            if (!trans['isEntered']) {
                trans['isEntered'] = true;

                // Store state and dispatch
                this.lastRoutesRecognized = this.router.globals.current;
                this.lastParams = { ...this.router.globals.params };

                // Build target url
                const targetState: StateObject = trans.$to();
                let targetURL: string = this.router.stateService.href(
                    targetState,
                    { ...trans.params('to') },
                    { inherit: true }
                );
                if (targetURL?.charAt(0) === '#') {
                    targetURL = targetURL.slice(1, targetURL.length);
                }

                this.routerState = {
                    url: targetURL,
                    name: targetState.name,
                    params: trans.params('to'),
                    fromState: {
                        url: this.lastRoutesRecognized.url,
                        name: this.lastRoutesRecognized.name,
                        params: this.lastParams
                    }
                } as IAppRouterState;

                if (this.shouldDispatchRouterNavigation()) {
                    this.dispatchRouterNavigation();
                }

                if (
                    this.cancelNavigation &&
                    this.cancelNavigation.length &&
                    trans.to().name === this.cancelNavigation
                ) {
                    this.cancelNavigation = null;
                    this.cancelNavigationOptions = null;
                    trans.abort();
                }
            }
        });
    
        // this is used to explicitly set the the router state on the store when navigating from child to parent
        // since the `onEnter` for parent is not triggered when moving from child to parent unless reload: true is passed
        this.router.transitionService.onExit({}, (trans: Transition) => {
            const targetState = trans.$to();
            const current = this.router.globals.current;
            const options = trans.options();

            // only if navigating from child to parent and the child is not explicitly setting reload to true
            if (
                current.name.indexOf(targetState.name) === 0 &&
                !options.reload
            ) {
                let targetURL: string = this.router.stateService.href(
                    targetState.name,
                    { ...trans.params('to') },
                    { inherit: true }
                );
                if (targetURL.charAt(0) === '#') {
                    targetURL = targetURL.slice(1, targetURL.length);
                }

                this.routerState = {
                    url: targetURL,
                    name: targetState.name,
                    params: trans.params('to'),
                    fromState: {
                        url: current.url,
                        name: current.name,
                        params: trans.params('from')
                    }
                } as IAppRouterState;

                this.store$.dispatch(
                    new SetRouterStateAction({
                        routerState: this.routerState,
                    })
                );

                // dispatched when navigating from child to parent and reload: true isn't set
                // cos ROUTER_NAVIGATION isn't dispatched, cos onEnter never triggers
                this.store$.dispatch(
                    new RouterToChildNavigation({
                        routerState: this.routerState,
                    })
                );
            }
        });

        this.router.transitionService.onSuccess({}, (trans: Transition) => {
            // not dispatching the router state as payload and reading it inside effect using `withLatestFrom` to ensure
            // latest state is available set from `onEnter` OR `onExit` hooks
            this.store$.dispatch(new RouterNavigationSuccessAction());
        });
    }

    /**
     * @return {?}
     */
    private dispatchRouterNavigation() {
        this.dispatchRouterAction(ROUTER_NAVIGATION, {
            routerState: this.routerState,
        });
    }

    /**
     * @param {?} event
     * @return {?}
     */
    private dispatchRouterCancel(event) {
        this.dispatchRouterAction(ROUTER_CANCEL, {
            routerState: this.routerState,
            event,
        });
    }
    
    /**
     * @param {?} event
     * @return {?}
     */
    private dispatchRouterError(event) {
        this.dispatchRouterAction(ROUTER_ERROR, {
            routerState: this.routerState,
            event,
        });
    }

    /**
     * @param {?} type
     * @param {?} payload
     * @return {?}
     */
    private dispatchRouterAction(type, payload) {
        this.dispatchTriggeredByRouter = true;
        try {
            this.store$.dispatch({ type, payload });
        } finally {
            this.dispatchTriggeredByRouter = false;
            this.navigationTriggeredByDispatch = false;
        }
    }

    private shouldCancelNavigation(state: string, options?: any) {
        this.cancelNavigation = state;
        this.cancelNavigationOptions = options;
    }
}
